import { ErrorPopup, SuccessPopup, useAlertContext, WarningPopup } from "@rhc-shared-components/alert-component";
import type { IRadioItem } from "@rhc-shared-components/form-radio-select";
import type { ISelectItem } from "@rhc-shared-components/form-select-component";
import { useIsMutating, useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import * as Yup from "yup";
import { createPfsNonContainerProject, syncTaticIds } from "../../../api/pfs";
import { createProject, patchProductListing } from "../../../api/pyxis";
import { isNameValid } from "../../../constants/formValidationSchemas";
import type { IErrorType, PfsNonContainerProjectPayload } from "../../../constants/pfsContants";
import { ProductKeys } from "../../../constants/product";
import { ProjectKeys as IProjectKeys } from "../../../constants/project";
import { RED_HAT_NAMING_GUIDELINE_ERROR } from "../../../constants/validation";
import { useProductMetadata } from "../../../hooks/useProductMetadata";
import type { ProjectListGraphQLResponseDataEntity } from "../../../interfaces/graphql";
import type { IPyxisAPIError } from "../../../interfaces/pyxis";
import { invalidateProductQueries, productDetailQueryKey, useProductDetail } from "../../../queries/productDetails";
import type { ProductListing } from "../../../types/generated-graphql";
import { transposeErrorMessages } from "../../../utils/parseErrors";
import { DeployedAsSetType, getAttachedProjectBadgeSet, getDeployedAsSet } from "../../../utils/productDeployedAsSet";

export const CreateFormKeys = {
	componentName: "componentName",
	componentType: "componentType",
	chartName: "chartName",
	operatorDistribution: "operatorDistribution",
	distributionMethod: "distributionMethod",
	osContentTypes: "osContentTypes",
	redhat: "redhat",
	badges: "badges",
	productVersion: "product_version",
	productName: "product_name",
	//container internal flag keys
	isSpecializedCert: "isSpecializedCert",
	openStackVersion: "openstack_name",
	partOfRhopDist: "partOfRhopDist",
	services: "services",
	allowHostedContainerRegistry: "allow_hosted_container_registry",
	helmDistributionMethodType: "helmDistributionMethodType",
	certProjects: "cert_projects",
};

export const ProjectCreateModalKeys = {
	id: "_id",
	badges: "badges",
	certificationDate: "certification_date",
	certificationStatus: "certification_status",
	certificationUser: "certification_user",
	container: "container",
	creationDate: "creation_date",
	drupal: "drupal",
	helmChart: "helm_chart",
	lastUpdateDate: "last_update_date",
	legacyProductID: "legacy_product_id",
	marketplace: "marketplace",
	name: "name",
	operatorDistribution: "operator_distribution",
	openstack: "openstack",
	productListings: "product_listings",
	projectStatus: "project_status",
	publishedBy: "published_by",
	redhat: "redhat",
	selfCertification: "self_certification",
	contacts: "contacts",
	type: "type",
	allowHostedRegistry: "hosted_registry",
} as const;

export const ProjectContainerKeys = {
	links: "_links",
	buildService: "build_service",
	buildCategories: "build_categories",
	autoPublish: "auto_publish",
	autoRebuild: "auto_rebuild",
	applicationCategories: "application_categories",
	distributionApproval: "distribution_approval",
	distributionMethod: "distribution_method",
	dockerConfigJson: "docker_config_json",
	githubUsernames: "github_usernames",
	hostedRegistry: "hosted_registry",
	isvPID: "isv_pid",
	kubeObjects: "kube_objects",
	osContentType: "os_content_type",
	passedRHCert: "passed_rhcert",
	privileged: "privileged",
	registry: "registry",
	registryCredentials: "registry_credentials",
	registryOverrideInstruct: "registry_override_instruct",
	registryVendor: "registry_vendor",
	releaseCategory: "release_category",
	repository: "repository",
	repositoryName: "repository_name",
	repositoryDescription: "repository_description",
	serviceAccountSecret: "service_account_secret",
	supportPlatforms: "support_platforms",
	shortDescription: "short_description",
	sourceUri: "source_uri",
	sourceSshPrivateKey: "source_ssh_private_key",
	sourceRef: "source_ref",
	sourceContextDir: "source_context_dir",
	sourceDockerfile: "source_dockerfile",
	type: "type",
	projectStatus: "projectStatus",
};

export enum ComponentTypes {
	Containers = "container",
	HelmChart = "helm chart",
	OpenShiftCNF = "OpenShift-cnf",
	OperatorBundle = "operator bundle image",
	openStackInfra = "openstack-infra",
	openStackInfraNonContainer = "OpenStack-infra-noncontainer",
	openStackInfraContainer = "OpenStack-infra-container",
	openStackVnf = "OpenStack-vnf",
	openStackAppContainer = "OpenStack-app-container",
	RHEL = "RHEL",
	OpenShiftVirtualization = "OpenShift-virtualization",
}

export enum EOSContentTypes {
	OperatorBundleImage = "Operator Bundle Image",
	UBI = "Red Hat Universal Base Image (UBI)",
	RHEL = "Red Hat Enterprise Linux",
}

export const OSContentTypes: IRadioItem[] = [
	{
		name: "Red Hat Universal Base Image",
		value: EOSContentTypes.UBI,
		description:
			"Images built with the Red Hat universal Base image can be distributed through Red Hat Container registry or a non Red Hat Registry.",
	},
	{
		name: "Red Hat Enterprise Linux",
		value: EOSContentTypes.RHEL,
		description: "Images built with RHEL 7 base image can only be distributed through Red Hat Container Registry.",
	},
];

export enum ProjectDistributionMethodName {
	redHatContainerRegistry = "rhcc",
	nonRegistry = "non_registry",
	external = "external",
	marketplaceOnly = "marketplace_only",
	redhat = "redhat",
}

export const getDistributionMethodType = (values?: string | boolean) => {
	const DistributionMethodType: ISelectItem[] = [
		{
			value: "Red Hat Container Registry",
			key: ProjectDistributionMethodName.redHatContainerRegistry,
			isDisabled: false,
			description:
				"Your certified containers are made available on Red Hat platforms without additional registry steps by customers.",
		},
		{
			value: "Your own Container Registry",
			key: ProjectDistributionMethodName.external,
			isDisabled: values === "Red Hat Enterprise Linux",
			description:
				"Customers will need to add your registry to their Red Hat platforms to install your certified containers.",
		},
	];
	return DistributionMethodType;
};

export const SpecializedCertificationOptions: IRadioItem[] = [
	{
		name: "Container Network Interface (CNI)",
		value: "cni",
	},
	{
		name: "Container Storage Interface (CSI)",
		value: "csi",
	},
];

export const OperatorDistributionOptions: ISelectItem[] = [
	{
		value: "Web catalog only (catalog.redhat.com)",
		key: "undistributed",
		description:
			"Your product will not be visible to customers within Red Hat Openshift's OperatorHub or Red Hat Marketplace indices.",
	},
	{
		value: "OpenShift In-product Catalog (Certified)",
		key: "connect",
		description: "List on catalog.redhat.com, and publish to the Certified operator index in OpenShift's OperatorHub.",
	},
];

export enum ProjectOperatorPublicationMethod {
	redHatCertified = "OpenShift In-product Catalog (Certified)",
	undistributed = "Web catalog only (catalog.redhat.com)",
}

export enum HelmProjectDistributionMethod {
	redhat = "Helm Chart repository (charts.openshift.io)",
	external = "External helm chart repository",
	undistributed = "Web catalog only (catalog.redhat.com)",
}

export enum HelmProjectDistributionMethodName {
	redhat = "redhat",
	external = "external",
	undistributed = "undistributed",
}

export const DistributionMethodTypeHelm: ISelectItem[] = [
	{
		value: HelmProjectDistributionMethod.redhat,
		key: HelmProjectDistributionMethodName.redhat,
		isDisabled: false,
		description: "Certified charts are placed in helm-charts.openshift.io and added to the repository index",
	},
	{
		value: HelmProjectDistributionMethod.undistributed,
		key: HelmProjectDistributionMethodName.undistributed,
		isDisabled: false,
		description:
			"Certified charts are only available on catalog.redhat.com and not distributed to any repository index",
	},
];

//Utilities function to create new component and attach it
export const useCreateAndAttachComponent = (handleCreateModal: (showModal: boolean) => void, _modaltype: string) => {
	const { productDetail, certificationListState, setCertificationListState } = useProductMetadata();
	const cert_projects = productDetail?.[ProductKeys.certProjects] || [];
	const { setAlertState } = useAlertContext();
	const queryClient = useQueryClient();
	const { isFetching: isProductDetailsLoading } = useProductDetail(productDetail?.[ProductKeys.id]);
	// eslint-disable-next-line @typescript-eslint/no-explicit-any -- accepts both project entities and create request objects
	const checkAttachmentError = useCallback(
		(newComponentList: any[], replace?: boolean): boolean => {
			let isAttachError = false;
			const oldList = replace ? newComponentList : certificationListState;
			const existingAttachedBadgesArr = oldList.filter((elm) => elm.badges)?.map((elm) => elm?.badges?.[0]);
			const attachedProjectTypeSet = getDeployedAsSet(oldList || []);
			const existingBadgesSet = getAttachedProjectBadgeSet(oldList || []);
			const newBadgesSet = getAttachedProjectBadgeSet(newComponentList || []);
			const combinedSet = new Set([...Array.from(existingBadgesSet), ...Array.from(newBadgesSet)]);
			// CNF + virt Special validation: https://issues.redhat.com/browse/CONNCERT-2398
			const allowSpecialCertFlag =
				(combinedSet.has("cnf") && combinedSet.has("virt") && combinedSet.size === 2) ||
				newComponentList.map((elm) => {
					let allowSpecialCert = true;
					if (elm.badges) {
						allowSpecialCert = existingAttachedBadgesArr.includes(
							Array.isArray(elm?.badges) ? elm?.badges?.[0] : elm?.badges,
						);
					}
					return allowSpecialCert;
				})[0];
			if (existingAttachedBadgesArr?.length > 0 && !allowSpecialCertFlag) {
				WarningPopup(
					setAlertState,
					"Cannot attach multiple specialized certification projects (CNI / CSI / CNF) of different types",
				);
				isAttachError = true;
			}
			if (
				(attachedProjectTypeSet.has(DeployedAsSetType.helmChart) ||
					attachedProjectTypeSet.has(DeployedAsSetType.operator)) &&
				(attachedProjectTypeSet.has(DeployedAsSetType.rhel) ||
					attachedProjectTypeSet.has(DeployedAsSetType.openstackAppContainer))
			) {
				WarningPopup(
					setAlertState,
					"It is not possible to attach RHEL or Openstack projects with Helm or OBI Projects",
				);
				isAttachError = true;
			}
			return isAttachError;
		},
		[certificationListState, setAlertState],
	);
	const createNewComponentMutation = useMutation({
		mutationFn: (request: Record<string, unknown>) => createProject(request),
		mutationKey: ["createComponent"],
		onSuccess: async (data) => {
			cert_projects.push(data._id);
			const productId = productDetail[ProductKeys.id] ?? "";
			const payload: Partial<ProductListing> = {
				[ProductKeys.id]: productId,
				cert_projects: cert_projects,
			};
			syncTaticIds(data?.[IProjectKeys.id], "CERTIFICATION_STATUS_STARTED");
			// sendComponentCreatedEvent(productDetail[ProductKeys.id], data._id)
			attachComponentToProduct.mutate(payload, {
				onSuccess: async () => {
					setCertificationListState([
						data as unknown as ProjectListGraphQLResponseDataEntity,
						...certificationListState,
					]);
					queryClient.invalidateQueries({ queryKey: productDetailQueryKey(productId) });
					invalidateProductQueries(queryClient, productId);
					SuccessPopup(setAlertState, "component created and attached successfully");
					handleCreateModal(false);
					document.body.classList.remove("stop-scrolling");
				},
			});
		},
		onError: (error: IPyxisAPIError) => {
			ErrorPopup(setAlertState, transposeErrorMessages(error?.detail ?? ""));
			document.body.classList.remove("stop-scrolling");
		},
	});

	const attachComponentToProduct = useMutation({
		mutationFn: (request: Partial<ProductListing>) => patchProductListing(request),
		mutationKey: ["attachComponent"],
		onError: (error: IPyxisAPIError) => {
			if (error.status === 403) {
				ErrorPopup(setAlertState, error.detail);
			} else {
				ErrorPopup(setAlertState, error.detail);
			}
			handleCreateModal(false);
			document.body.classList.remove("stop-scrolling");
		},
	});
	const isCreateComponentAPILoading = useIsMutating({ mutationKey: ["createComponent"] }) > 0;
	const isAttachComponentAPILoading = useIsMutating({ mutationKey: ["attachComponent"] }) > 0;
	const createNewComponent = useCallback(
		async (request: Record<string, unknown>) => {
			if (checkAttachmentError([request])) {
				return false;
			}
			await createNewComponentMutation.mutateAsync(request);
		},
		[createNewComponentMutation, checkAttachmentError],
	);
	return {
		createNewComponent,
		attachComponentToProduct,
		isCreateComponentAPILoading,
		isAttachComponentAPILoading,
		checkAttachmentError,
		isProductDetailsLoading,
	};
};

export const useCreateAndAttachNonContainerComponent = (
	handleCreateModal: (showModal: boolean) => void,
	_modalType: string,
) => {
	const { attachComponentToProduct } = useCreateAndAttachComponent(handleCreateModal, _modalType);
	const { productDetail } = useProductMetadata();
	const cert_projects = productDetail?.[ProductKeys.certProjects] || [];
	const queryClient = useQueryClient();
	const { setAlertState } = useAlertContext();
	const { isFetching: isProductDetailsLoading } = useProductDetail(productDetail?.[ProductKeys.id]);
	const createNewNonContainerComponent = useMutation({
		mutationFn: (request: PfsNonContainerProjectPayload) => createPfsNonContainerProject(request),
		mutationKey: ["createNewNonContainerComponent"],
		onSuccess: async (data) => {
			cert_projects.push(data.pyxisId);
			const productId = productDetail[ProductKeys.id] ?? "";
			const payload: Partial<ProductListing> = {
				[ProductKeys.id]: productId,
				cert_projects: cert_projects,
			};
			syncTaticIds(data.pyxisId, "CERTIFICATION_STATUS_STARTED");
			// sendComponentCreatedEvent(productDetail[ProductKeys.id], data.pyxisId)
			attachComponentToProduct.mutate(payload, {
				onSuccess: async () => {
					await queryClient.invalidateQueries({ queryKey: productDetailQueryKey(productId) });
					invalidateProductQueries(queryClient, productId);
					SuccessPopup(setAlertState, "component created and attached successfully");
					handleCreateModal(false);
					document.body.classList.remove("stop-scrolling");
				},
			});
		},
		onError: (error: IErrorType) => {
			ErrorPopup(setAlertState, transposeErrorMessages(error?.detailMessage ?? ""));
			document.body.classList.remove("stop-scrolling");
		},
	});
	const isCreateNonContainerComponentAPILoading =
		useIsMutating({ mutationKey: ["createNewNonContainerComponent"] }) > 0;
	return {
		createNewNonContainerComponent,
		isCreateNonContainerComponentAPILoading,
		isProductDetailsLoading,
	};
};
export const REQUIRED_MESSAGE = "This field is required";
export const MAX_255_MESSAGE = "This field should be less than or equal to 255 characters";
export const ContainerCreateInitialValues = {
	[CreateFormKeys.osContentTypes]: "",
	[CreateFormKeys.distributionMethod]: "",
	[CreateFormKeys.allowHostedContainerRegistry]: false,
};

export const ContainerCreateValidators: Yup.ObjectSchema = Yup.object().shape({
	[CreateFormKeys.osContentTypes]: Yup.string().required(REQUIRED_MESSAGE),
	[CreateFormKeys.distributionMethod]: Yup.string().required(REQUIRED_MESSAGE),
	[CreateFormKeys.allowHostedContainerRegistry]: Yup.boolean(),
});

export const OperatorCreateInitialValues = {
	[CreateFormKeys.distributionMethod]: ProjectDistributionMethodName.redHatContainerRegistry,
	[CreateFormKeys.operatorDistribution]: ProjectOperatorPublicationMethod.undistributed,
	[CreateFormKeys.isSpecializedCert]: false,
	[CreateFormKeys.badges]: "cni",
};

export const OperatorCreateValidator: Yup.ObjectSchema = Yup.object().shape({
	[CreateFormKeys.distributionMethod]: Yup.string().required(REQUIRED_MESSAGE),
	[CreateFormKeys.operatorDistribution]: Yup.string().required(REQUIRED_MESSAGE),
	[CreateFormKeys.isSpecializedCert]: Yup.boolean(),
	[CreateFormKeys.badges]: Yup.string(),
});

export const HelmCreateInitialValues = {
	[CreateFormKeys.chartName]: "",
	[CreateFormKeys.distributionMethod]: HelmProjectDistributionMethod.redhat,
	[CreateFormKeys.helmDistributionMethodType]: false,
};

export const ComponentCreateInitialValues = {
	[CreateFormKeys.componentName]: "",
	[CreateFormKeys.componentType]: "",
	[CreateFormKeys.productVersion]: "17",
};

export const RhelCreateInitialValues = {
	[CreateFormKeys.distributionMethod]: "",
	[CreateFormKeys.productVersion]: "8",
	[CreateFormKeys.allowHostedContainerRegistry]: false,
};

export const OpenstackCreateModalInitialValues = {
	[CreateFormKeys.componentName]: "",
	[CreateFormKeys.componentType]: "",
	[CreateFormKeys.productVersion]: "",
};

export const StandaloneCreateInitialValues = {
	[CreateFormKeys.componentName]: "",
	[CreateFormKeys.componentType]: ComponentTypes.RHEL,
	[CreateFormKeys.productVersion]: "8",
};

export const ComponentCreateValidator: Yup.ObjectSchema = Yup.object().shape({
	[CreateFormKeys.componentName]: Yup.string()
		.required(REQUIRED_MESSAGE)
		.max(255, MAX_255_MESSAGE)
		.test("is-component-name-valid", RED_HAT_NAMING_GUIDELINE_ERROR, isNameValid),
	[CreateFormKeys.componentType]: Yup.string().required(REQUIRED_MESSAGE),
	[CreateFormKeys.productVersion]: Yup.string().when([CreateFormKeys.componentType], {
		is: "OpenStack-app-container",
		then: Yup.string().trim().required("Red Hat OpenStack version is required"),
	}),
});

export const OpenstackCreateModalValidator: Yup.ObjectSchema = Yup.object().shape({
	[CreateFormKeys.productVersion]: Yup.string().trim().required("Red Hat OpenStack version is required"),
});

export const StandaloneCreateModalValidator: Yup.ObjectSchema = Yup.object().shape({
	[CreateFormKeys.productVersion]: Yup.string().trim().required("RHEL version is required"),
});
export const HelmCreateValidator: Yup.ObjectSchema = Yup.object().shape({
	[CreateFormKeys.chartName]: Yup.string()
		.trim()
		.max(255, "This field should be less than or equal to 255 characters")
		.matches(
			/^[a-z]+[-a-z\d]*$/,
			"Must be lower case letters or numbers, and start with a letter. Words may be separated with dashes (-)",
		)
		.required("This field is required")
		.test("is-chart-name-valid", RED_HAT_NAMING_GUIDELINE_ERROR, isNameValid),
	[CreateFormKeys.distributionMethod]: Yup.string().required(REQUIRED_MESSAGE),
	[CreateFormKeys.helmDistributionMethodType]: Yup.boolean().notRequired(),
});
export const OpenStackInfraProjectInitialValue = {
	[CreateFormKeys.services]: "",
	[CreateFormKeys.partOfRhopDist]: "",
};
export const OpenStackInfraValidators: Yup.ObjectSchema = Yup.object().shape({
	[CreateFormKeys.partOfRhopDist]: Yup.string().trim().required(REQUIRED_MESSAGE),
	[CreateFormKeys.services]: Yup.string().trim().required(REQUIRED_MESSAGE),
});

export const RhelCreateValidators: Yup.ObjectSchema = Yup.object().shape({
	[CreateFormKeys.distributionMethod]: Yup.string().required(REQUIRED_MESSAGE),
	[CreateFormKeys.productVersion]: Yup.string().trim().required("Rhel version is required"),
	[CreateFormKeys.allowHostedContainerRegistry]: Yup.boolean(),
});

export const ExistingAttachedComponentValidator: Yup.ObjectSchema = Yup.object().shape({
	[CreateFormKeys.certProjects]: Yup.array().min(1, "At least one selection is required").nullable(true),
});
export const ContainerStackComponentTypes: IRadioItem[] = [
	{
		name: "Operator Bundle",
		value: ComponentTypes.OperatorBundle,
		description:
			"Certify an Operator that manages an OpenShift application built from certified container images and their life cycles.",
	},
	{
		name: "Helm Chart",
		value: ComponentTypes.HelmChart,
		description: "Certify a Helm chart that manages an OpenShift application built from certified container images.",
	},
	{
		name: "Container Image",
		value: ComponentTypes.Containers,
		description:
			"Certify a stand-alone software product or components of a software product packaged as container images.",
	},
	{
		name: "Cloud-native Network Function (CNF)",
		value: ComponentTypes.OpenShiftCNF,
		description: "Certify a specialized product for Telecommunications.",
	},
	{
		name: "Containerized application for RHEL",
		value: ComponentTypes.RHEL,
		description: "Certify a containerized application on Red Hat Enterprise Linux",
	},
	{
		name: "Containerized application for OpenStack",
		value: ComponentTypes.openStackAppContainer,
		description: "Certify a containerized application for Red Hat OpenStack Platform.",
	},
];

export const OpenStackInfraPlatformComponentCreateTypes: IRadioItem[] = [
	{
		name: "OpenStack Infrastructure",
		value: ComponentTypes.openStackInfra,
		description: "Certify a plugin/driver that uses your own container images for Red Hat OpenStack Platform.",
	},
	{
		name: "VNF for OpenStack",
		value: ComponentTypes.openStackVnf,
		description: "Certify a Virtual Network Function packaged as a virtual machine for Red Hat OpenStack Platform.",
	},
];

export const OpenStackVersions: IRadioItem[] = [
	{
		name: "17",
		value: "17",
		description: "",
	},
	{
		name: "18",
		value: "18",
		description: "",
	},
];
export const ProjectNonContKeys = {
	certificationStatus: "certificationStatus",
	name: "name",
	projectStatus: "projectStatus",
	rhProductName: "redhatProductName",
	rhProductVersion: "redhatProductVersion",
	type: "type",
	pyxisId: "pyxisId",
	contacts: "contacts",
} as const;

//OpenStack create constants
export const OpenStackPfDistOptions: IRadioItem[] = [
	{
		name: "Yes, my product has already been contributed to the OpenStack distribution",
		value: "yes",
	},
	{
		name: "No, my product is not contributed to the OpenStack distribution",
		value: "no",
	},
];

export const OpenStackInfraServicesOptions: ISelectItem[] = [
	{
		value: "Neutron",
		key: "Neutron",
	},
	{
		value: "Cinder",
		key: "Cinder",
	},
	{
		value: "Manila",
		key: "Manila",
	},
];

export const RHELVersions: IRadioItem[] = [
	{
		name: "8",
		value: "8",
		description: "",
	},
	{
		name: "9",
		value: "9",
		description: "",
	},
	{
		name: "10",
		value: "10",
		description: "",
	},
];

export const StandalonePlatformCreateComponentTypes: IRadioItem[] = [
	{
		name: "Non-containerized Application",
		value: ComponentTypes.RHEL,
		description: "Certify a non-containerized application on Red Hat Enterprise Linux",
	},
];

export enum RhelCreateConstants {
	RHEL = "RHEL",
	RHEL_FULLNAME = "Red Hat Enterprise Linux",
	RHEL_CODE = "rhel",
}
